import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { redeemOverrideOtp } from "@/shared/api/overrides";
import {
  createPriceList,
  deactivatePriceList,
  getPriceList,
  listPriceLists,
  listProducts,
  type PriceListRow,
  updatePriceList,
  upsertPriceListItems,
} from "@/shared/api/inventory";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { priceListColumns } from "./price-lists-columns";

const priceListCreateSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  isDefaultPos: z.boolean(),
});
type PriceListCreateInput = z.input<typeof priceListCreateSchema>;

/** Advanced: lists + POS default + product prices — plan/31 */
export function PriceListsPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const confirmUi = useConfirm();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [pendingChangeIds, setPendingChangeIds] = useState<string[]>([]);
  const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false);

  const listsQuery = useQuery({
    queryKey: queryKeys.priceLists.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listPriceLists(companySlug!, { includeDeactivated: true }),
  });
  const productsQuery = useQuery({
    queryKey: queryKeys.products.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listProducts(companySlug!),
  });
  const detailQuery = useQuery({
    queryKey: queryKeys.priceLists.detail(
      companySlug ?? "",
      selectedId ?? "",
    ),
    enabled: Boolean(companySlug && selectedId),
    queryFn: () => getPriceList(companySlug!, selectedId!),
  });
  const errorMsg = queryErrorMessage(listsQuery.error, listsQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const items = detailQuery.data?.items ?? [];

  useEffect(() => {
    if (!selectedId) {
      setDraftPrices({});
      return;
    }
    if (!detailQuery.data) return;
    const map: Record<string, string> = {};
    for (const it of detailQuery.data.items) map[it.productId] = it.price;
    setDraftPrices(map);
  }, [selectedId, detailQuery.data]);

  useEffect(() => {
    const lists = listsQuery.data?.priceLists;
    if (!lists || !selectedId) return;
    if (!lists.some((l) => l.id === selectedId)) {
      setSelectedId(null);
    }
  }, [listsQuery.data, selectedId]);

  const createForm = useForm<PriceListCreateInput>({
    resolver: zodResolver(priceListCreateSchema),
    defaultValues: { name: "", isDefaultPos: false },
  });

  async function reloadLists() {
    if (!companySlug) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.priceLists.all(companySlug),
    });
  }

  async function reloadDetail() {
    if (!companySlug || !selectedId) return;
    await queryClient.invalidateQueries({
      queryKey: queryKeys.priceLists.detail(companySlug, selectedId),
    });
  }

  const create = useMutation({
    mutationFn: async (raw: PriceListCreateInput) => {
      const body = priceListCreateSchema.parse(raw);
      return createPriceList(companySlug!, {
        name: body.name,
        isDefaultPos: body.isDefaultPos,
      });
    },
    onSuccess: async ({ priceList }) => {
      createForm.reset({ name: "", isDefaultPos: false });
      setError(null);
      await reloadLists();
      setSelectedId(priceList.id);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  const columns = useMemo(
    () =>
      priceListColumns({
        busy,
        selectedId,
        selectedItemCount: items.length,
        onSelect: setSelectedId,
        onRename: (list: PriceListRow, nextName: string) => {
          const trimmed = nextName.trim();
          if (!trimmed || trimmed === list.name || !companySlug) return;
          setBusy(true);
          setError(null);
          void updatePriceList(companySlug, list.id, { name: trimmed })
            .then(() => reloadLists())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Update failed"),
            )
            .finally(() => setBusy(false));
        },
        onSetDefaultPos: (list: PriceListRow) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updatePriceList(companySlug, list.id, {
            isDefaultPos: true,
            reactivate: list.deactivatedAt != null,
          })
            .then(() => reloadLists())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
        onDeactivate: (list: PriceListRow) => {
          void (async () => {
            if (!companySlug) return;
            const ok = await confirmUi({
              title: `Deactivate “${list.name}”?`,
              description:
                "It will hide from new picks. Existing docs keep their prices; you can reactivate later.",
              confirmLabel: "Deactivate",
              destructive: true,
            });
            if (!ok) return;
            setBusy(true);
            setError(null);
            try {
              await deactivatePriceList(companySlug, list.id);
              await reloadLists();
            } catch (err: unknown) {
              setError(err instanceof Error ? err.message : "Failed");
            } finally {
              setBusy(false);
            }
          })();
        },
        onReactivate: (list: PriceListRow) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updatePriceList(companySlug, list.id, { reactivate: true })
            .then(() => reloadLists())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [busy, selectedId, items.length, companySlug, confirmUi],
  );

  if (!companySlug) return null;

  const lists = listsQuery.data?.priceLists ?? [];
  const products = productsQuery.data?.products ?? [];
  const selected = lists.find((l) => l.id === selectedId) ?? null;
  const activeProducts = products.filter((p) => p.deactivatedAt == null);
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  async function onSavePrices() {
    if (!selectedId) return;
    const payload = activeProducts
      .map((p) => {
        const price = (draftPrices[p.id] ?? "").trim();
        if (!price) return null;
        return { productId: p.id, price };
      })
      .filter((x): x is { productId: string; price: string } => x != null);
    if (payload.length === 0) {
      setError("Enter at least one price");
      return;
    }
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const res = await upsertPriceListItems(
        companySlug!,
        selectedId,
        payload,
      );
      if (res.applied) {
        setPendingChangeIds([]);
        setStatus(
          res.upserted === 0
            ? "No price changes"
            : `Saved ${res.upserted} price${res.upserted === 1 ? "" : "s"}`,
        );
        await reloadDetail();
      } else {
        setPendingChangeIds(res.changeRequests.map((r) => r.id));
        setStatus(
          `${res.queued} price change${res.queued === 1 ? "" : "s"} queued — Approvals or emergency OTP below`,
        );
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
      <PageFrame>
      <PageHeader
        title="Price lists"
        description="Multiple lists for POS, wholesale, and customer defaults. Changing product prices is a gated master update. Line prices on posted docs stay frozen."
      />

      {listsQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading price lists…
        </Alert>
      )}
      {!listsQuery.isPending && lists.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No price lists"
            description="Add a list for POS, wholesale, or customer defaults."
          />
        </div>
      ) : null}
      {!listsQuery.isPending && lists.length > 0 ? (
        <DataTable
          listId="price-lists"
          companySlug={companySlug}
          columns={columns}
          data={lists}
          getRowId={(r) => r.id}
          manualSorting={false}
          defaultVisibility={{ code: false }}
        />
      ) : null}

      <form
        className="mt-8 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={createForm.handleSubmit((values) => create.mutate(values))}
      >
        <h2 className="m-0 text-sm font-semibold">Add list</h2>
        <label className="block text-xs text-[var(--color-muted)]">
          Name
          <Input
            className="mt-1"
            placeholder="e.g. Wholesale"
            {...createForm.register("name")}
          />
        </label>
        {createForm.formState.errors.name && (
          <p className="text-xs text-[var(--color-destructive)]">
            {createForm.formState.errors.name.message}
          </p>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...createForm.register("isDefaultPos")} />
          Default for POS
        </label>
        <Button type="submit" disabled={busy || create.isPending}>
          Add
        </Button>
      </form>

      {selected && selected.deactivatedAt == null && (
        <div className="mt-10 border-t border-[var(--color-border)] pt-6">
          <h2 className="m-0 text-sm font-semibold">
            Prices · {selected.name}
          </h2>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            Blank = leave unset on this list. Blank unit price on SO uses the
            resolved list.
          </p>
          {detailQuery.isPending && (
            <Alert variant="muted" className="mt-4">
              Loading prices…
            </Alert>
          )}
          <ul className="mt-4 m-0 list-none space-y-2 p-0">
            {activeProducts.map((p) => (
              <li
                key={p.id}
                className="grid grid-cols-[1fr_7rem] items-center gap-2"
              >
                <span className="truncate text-sm">
                  {p.name}
                  {p.sku ? (
                    <span className="text-[var(--color-muted)]">
                      {" "}
                      · {p.sku}
                    </span>
                  ) : null}
                </span>
                <Input
                  className="font-mono text-right tabular-nums"
                  placeholder="0.00"
                  inputMode="decimal"
                  value={draftPrices[p.id] ?? ""}
                  onChange={(e) =>
                    setDraftPrices((prev) => ({
                      ...prev,
                      [p.id]: e.target.value,
                    }))
                  }
                />
              </li>
            ))}
          </ul>
          {activeProducts.length === 0 && (
            <p className="mt-2 text-sm text-[var(--color-muted)]">
              No products yet.{" "}
              <Link to={`/c/${companySlug}/products/new`}>Add a product</Link>
            </p>
          )}
          <Button
            className="mt-4"
            type="button"
            disabled={busy || activeProducts.length === 0}
            onClick={() => void onSavePrices()}
          >
            Save prices
          </Button>
          {pendingChangeIds.length > 0 && (
            <label className="mt-4 block text-xs text-[var(--color-muted)]">
              Emergency OTP from manager (applies first queued change)
              <div className="mt-1 flex gap-2">
                <Input
                  className="min-w-0 flex-1 font-mono tracking-widest"
                  inputMode="numeric"
                  placeholder="••••••"
                  maxLength={8}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  disabled={busy}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || otpCode.trim().length < 4}
                  onClick={() => {
                    const requestId = pendingChangeIds[0];
                    if (!requestId) return;
                    setBusy(true);
                    setError(null);
                    void redeemOverrideOtp(companySlug, {
                      code: otpCode.trim(),
                      requestId,
                      requestKind: "change",
                    })
                      .then(async () => {
                        const rest = pendingChangeIds.slice(1);
                        setPendingChangeIds(rest);
                        setOtpCode("");
                        setStatus(
                          rest.length === 0
                            ? "OTP accepted — price applied"
                            : `OTP accepted — ${rest.length} still queued`,
                        );
                        await reloadDetail();
                      })
                      .catch((err: unknown) =>
                        setError(
                          err instanceof Error ? err.message : "OTP failed",
                        ),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  Use
                </Button>
              </div>
            </label>
          )}
        </div>
      )}

      {status && (
        <Alert variant="muted" className="mt-4">
          {status}
        </Alert>
      )}
      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <p className="mt-8 text-sm">
        <Link to={`/c/${companySlug}/approvals`} className="no-underline">
          Approvals
        </Link>
        {" · "}
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
